import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { StructureStyleSelect } from "~/components/assistant/messages/StructureStyleSelect";
import {
  DEFAULT_PROTEIN_VIEW_STYLE,
  type ProteinViewStyle,
} from "~/components/assistant/messages/proteinViewerStyle";
import { cn } from "~/lib/utils";
import { type ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";
import { filenameFromPreview, hasFileSuffix } from "./inlineViewerPreview";
import type { StructureInput, StructureViewer } from "./molstarViewer";
import { useInlineViewerUrl } from "./useInlineViewerUrl";

const STRUCTURE_SUFFIXES = [".pdb", ".cif", ".mmcif", ".bcif"] as const;
const MAX_STRUCTURE_FILE_BYTES = 20 * 1024 * 1024;

export function isProteinStructureArtifact(preview: ArtifactPreview): boolean {
  return hasFileSuffix(preview, STRUCTURE_SUFFIXES);
}

function structureFormat(fileName: string): StructureInput["format"] {
  return fileName.toLowerCase().endsWith(".pdb") ? "pdb" : "mmcif";
}

function isBinaryCif(fileName: string): boolean {
  return fileName.toLowerCase().endsWith(".bcif");
}

interface ProteinViewerProps {
  preview: ArtifactPreview;
  className?: string;
}

const ProteinViewer = ({ preview, className }: ProteinViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewer, setViewer] = useState<StructureViewer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [style, setStyle] = useState<ProteinViewStyle>(DEFAULT_PROTEIN_VIEW_STYLE);
  const filename = filenameFromPreview(preview);
  const url = useInlineViewerUrl(preview);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let created: StructureViewer | null = null;

    const loadStructure = async () => {
      try {
        setIsLoading(true);
        setViewer(null);
        setError(null);

        if (!url) {
          throw new Error("No content URL available for this artifact.");
        }

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        const contentLength = response.headers.get("Content-Length");
        if (contentLength && Number(contentLength) > MAX_STRUCTURE_FILE_BYTES) {
          throw new Error("File is too large to render inline. Please download it instead.");
        }

        const data = isBinaryCif(filename)
          ? new Uint8Array(await response.arrayBuffer())
          : await response.text();
        if (cancelled) return;

        const { createStructureViewer } = await import("./molstarViewer");
        if (cancelled) return;

        created = await createStructureViewer(container, {
          data,
          format: structureFormat(filename),
          label: filename,
        });
        if (cancelled) {
          created.dispose();
          return;
        }
        setViewer(created);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load protein structure:", err);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadStructure();

    return () => {
      cancelled = true;
      created?.dispose();
    };
  }, [url, filename]);

  // Apply the representation mode once the structure is loaded, and whenever
  // the user changes it. Kept separate from loading so switching modes never
  // re-downloads the structure.
  useEffect(() => {
    if (!viewer) return;
    viewer.setStyle(style).catch((err: unknown) => {
      console.error("Failed to apply structure style:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    });
  }, [viewer, style]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className ?? "h-[450px]")}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-muted-foreground animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading structure...</p>
          </div>
        </div>
      )}
      {!isLoading && (
        <div className="absolute top-2 right-2 z-10">
          <StructureStyleSelect value={style} onChange={setStyle} />
        </div>
      )}
      <div
        ref={containerRef}
        className={cn("relative w-full h-full rounded-md", isLoading && "invisible")}
      />
    </div>
  );
};

export const ProteinStructureArtifact = ({ preview }: { preview: ArtifactPreview }) => (
  <ArtifactShell
    preview={preview}
    expandedContent={<ProteinViewer preview={preview} className="h-full" />}
  >
    <FileArtifactBody preview={preview} />
    <div className="mt-3">
      <ProteinViewer preview={preview} />
    </div>
  </ArtifactShell>
);
